/**
 * @module stores/authStore
 * @description Authentication state management
 *
 * Manages authentication state via Supabase proxy mode including:
 * - Supabase session management
 * - User ID tracking
 * - Authentication status
 * - Login/logout flows
 *
 * Note: Direct API key authentication has been removed for security.
 * All authentication goes through Spectral Cloud Portal (Supabase proxy).
 *
 * @example
 * import { authStore, isAuthenticated, logout } from '@stores/authStore';
 *
 * // Check authentication and get user ID
 * $: userId = $isAuthenticated ? $authStore.userId : null;
 *
 * // Logout
 * logout();
 */

import { writable, derived, get } from 'svelte/store';
import { createLogger } from '@services/loggerService';
import { notify } from '@services/notificationService';
import { indexedDBService } from '@services/indexedDBService';
import { zoteroService } from '@services/zoteroService';
import {
  isAuthenticated as checkSupabaseAuth,
  isZoteroConfigured as checkZoteroConfigured,
  signInWithEmail,
  handleSsoFromUrl,
} from '@services/supabaseClient';
import { api } from '@/config';
import type { ApiKeyAccess } from '@types';

const log = createLogger('auth');

// =============================================================================
// TYPES
// =============================================================================

/** Authentication state */
export interface AuthState {
  /** API key (null if not authenticated, or 'proxy' in proxy mode) */
  apiKey: string | null;
  /** User ID from Zotero */
  userId: number | null;
  /** Username from Zotero */
  username: string | null;
  /** API key access permissions */
  access: ApiKeyAccess | null;
  /** Whether authentication is in progress */
  isLoading: boolean;
  /** Authentication error message */
  error: string | null;
  /** Proxy mode state */
  proxy: {
    /** Whether Supabase session is active */
    isSupabaseAuthenticated: boolean;
    /** Whether Zotero API key is configured in Portal */
    isZoteroConfigured: boolean;
  };
}

/** Login result */
export interface LoginResult {
  success: boolean;
  error?: string;
  userId?: number;
}

// =============================================================================
// INITIAL STATE
// =============================================================================

const initialState: AuthState = {
  apiKey: null,
  userId: null,
  username: null,
  access: null,
  isLoading: false,
  error: null,
  proxy: {
    isSupabaseAuthenticated: false,
    isZoteroConfigured: false,
  },
};

// =============================================================================
// STORE
// =============================================================================

/** Internal writable store */
const store = writable<AuthState>(initialState);

/** Exported auth store (read-only subscribe) */
export const authStore = {
  subscribe: store.subscribe,
};

// =============================================================================
// DERIVED STORES
// =============================================================================

/** Whether user is authenticated */
export const isAuthenticated = derived(
  store,
  ($store) => $store.apiKey !== null && $store.userId !== null
);

/** Whether authentication is loading */
export const isAuthLoading = derived(store, ($store) => $store.isLoading);

/** Current user ID */
export const userId = derived(store, ($store) => $store.userId);

/** Current username */
export const username = derived(store, ($store) => $store.username);

/** Authentication error */
export const authError = derived(store, ($store) => $store.error);

/** API key access permissions */
export const apiAccess = derived(store, ($store) => $store.access);

/** Whether user has write access to personal library */
export const canWritePersonal = derived(store, ($store) => $store.access?.user?.library === true);

/** Whether proxy mode is enabled */
export const isProxyMode = derived(store, () => api.proxy.enabled);

/** Whether Supabase is authenticated (proxy mode) */
export const isSupabaseAuthenticated = derived(
  store,
  ($store) => $store.proxy.isSupabaseAuthenticated
);

/** Whether Zotero key is configured (proxy mode) */
export const isZoteroConfigured = derived(store, ($store) => $store.proxy.isZoteroConfigured);

// =============================================================================
// ACTIONS
// =============================================================================

/**
 * Initialize auth (checks Supabase session)
 */
export async function initAuth(): Promise<void> {
  log.info('Initializing authentication');
  await initAuthProxy();
}

/**
 * Initialize auth in proxy mode (check Supabase session)
 */
export async function initAuthProxy(): Promise<void> {
  log.info('Initializing proxy mode authentication');
  store.update((s) => ({ ...s, isLoading: true, error: null }));

  try {
    // First, check for SSO tokens in URL (from Portal redirect)
    await handleSsoFromUrl();

    const isSupabaseAuth = await checkSupabaseAuth();
    const isZoteroConf = isSupabaseAuth ? await checkZoteroConfigured() : false;

    store.update((s) => ({
      ...s,
      isLoading: false,
      proxy: {
        isSupabaseAuthenticated: isSupabaseAuth,
        isZoteroConfigured: isZoteroConf,
      },
    }));

    if (isSupabaseAuth && isZoteroConf) {
      // Auto-login in proxy mode
      await loginViaProxy();
    }

    log.info('Proxy auth check complete', { isSupabaseAuth, isZoteroConf });
  } catch (error) {
    log.error('Proxy auth check failed', { error });
    store.update((s) => ({
      ...s,
      isLoading: false,
      error: error instanceof Error ? error.message : 'Authentication check failed',
    }));
  }
}

/**
 * Refresh proxy mode authentication status
 */
export async function refreshProxyAuth(): Promise<void> {
  await initAuthProxy();
}

/**
 * Login to Supabase with email and password (proxy mode)
 */
export async function loginToSupabase(
  email: string,
  password: string
): Promise<{ success: boolean; error?: string }> {
  log.info('Attempting Supabase login');
  store.update((s) => ({ ...s, isLoading: true, error: null }));

  try {
    const result = await signInWithEmail(email, password);

    if (result.success) {
      // Check Zotero configuration after successful login
      const isZoteroConf = await checkZoteroConfigured();

      store.update((s) => ({
        ...s,
        isLoading: false,
        proxy: {
          isSupabaseAuthenticated: true,
          isZoteroConfigured: isZoteroConf,
        },
      }));

      notify.success('Connecté à Spectral Cloud');
      log.info('Supabase login successful');

      // If Zotero is configured, auto-login
      if (isZoteroConf) {
        await loginViaProxy();
      }

      return { success: true };
    } else {
      store.update((s) => ({
        ...s,
        isLoading: false,
        error: result.error ?? null,
      }));

      return { success: false, error: result.error };
    }
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Login failed';
    store.update((s) => ({
      ...s,
      isLoading: false,
      error: message,
    }));

    log.error('Supabase login failed', { error });
    return { success: false, error: message };
  }
}

/**
 * Login via proxy mode (API key stored in Portal)
 */
export async function loginViaProxy(): Promise<LoginResult> {
  log.info('Attempting proxy mode login');
  store.update((s) => ({ ...s, isLoading: true, error: null }));

  try {
    // Validate via proxy - this will get user info from the Zotero API
    const result = await zoteroService.validateApiKey('proxy');

    if (result.success) {
      const { userID, username, access } = result.data;

      store.update((s) => ({
        ...s,
        apiKey: 'proxy', // Special value indicating proxy mode
        userId: userID,
        username: username || null,
        access: access || null,
        isLoading: false,
        error: null,
        proxy: {
          isSupabaseAuthenticated: true,
          isZoteroConfigured: true,
        },
      }));

      notify.success('Connected via Spectral Cloud');
      log.info('Proxy login successful', { userId: userID });

      return { success: true, userId: userID };
    } else {
      store.update((s) => ({
        ...s,
        isLoading: false,
        error: result.error,
      }));

      notify.error(result.error || 'Proxy authentication failed');
      return { success: false, error: result.error };
    }
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Proxy login failed';
    store.update((s) => ({
      ...s,
      isLoading: false,
      error: message,
    }));

    notify.error(message);
    log.error('Proxy login failed', { error });
    return { success: false, error: message };
  }
}

/**
 * Logout and clear session
 */
export function logout(): void {
  log.info('Logging out');

  // Clear local storage preferences
  indexedDBService.clear();

  // Reset store
  store.set({ ...initialState });

  notify.info('Logged out successfully');
}

/**
 * Clear authentication error
 */
export function clearAuthError(): void {
  store.update((s) => ({ ...s, error: null }));
}

/**
 * Check if user has access to a specific group
 */
export function hasGroupAccess(groupId: number): boolean {
  const state = get(store);
  if (!state.access?.groups) return false;

  const group = state.access.groups[groupId];
  return group?.library === true;
}

/**
 * Check if user can write to a group
 */
export function canWriteToGroup(groupId: number): boolean {
  const state = get(store);
  if (!state.access?.groups) return false;

  const group = state.access.groups[groupId];
  return group?.write === true;
}

/**
 * Get current auth state (for debugging)
 */
export function getAuthState(): AuthState {
  return get(store);
}

// =============================================================================
// STORE RESET
// =============================================================================

/**
 * Reset store to initial state (for testing)
 */
export function resetAuthStore(): void {
  store.set({ ...initialState });
}

export default authStore;
